import React, {FC} from 'react';
import {PixelRatio} from 'react-native';
import {View} from 'react-native';

import RankIcon from '/component/base/RankIcon';
import Text from '/component/base/Text';
import {Rank} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './Summary.style';
import {getProgressDescription} from './Summary.util';

interface SummaryProps {
  tt: (key: string, value: string) => string;
  rankId: number;
  rankName: string;
  hasNextRank: boolean;
  percent: number;
  isUser: boolean;
  showDetailCta: boolean;
  onPress: () => void;
}

export const Summary: FC<SummaryProps> = ({
  tt = withTranslation.ttDefaultProp,
  rankId = 0,
  rankName = '',
  hasNextRank = true,
  percent = 0,
  isUser = true,
}: SummaryProps) => {
  const fontScale = Math.min(Math.max(PixelRatio.getFontScale(), 1), 2);
  const isLargeFont = fontScale > 1.2;
  const rankIconScale = isLargeFont ? 1.0 : 4 / 3;

  const baseViewProps = {
    style: styles.base,
  };

  const progressDescription = getProgressDescription({
    hasNextRank,
    isUser,
    percent,
    rankName,
    tt,
  });

  const getStringFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString();
    }

    if (React.isValidElement(node)) {
      const children = node.props.children;

      if (!children) return '';

      if (Array.isArray(children)) {
        return children.map(getStringFromReactNode).join('');
      } else {
        return getStringFromReactNode(children);
      }
    }

    if (Array.isArray(node)) {
      return node.map(getStringFromReactNode).join('');
    }

    // node is null, undefined, boolean, or something else
    return '';
  };

  const progressDescriptionString = getStringFromReactNode(progressDescription);
  return (
    <View {...testProperties(null, true)} {...baseViewProps}>
      <View
        style={styles.leadIn}
        accessible={true}
        accessibilityLabel={`Your Next Rank.`}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessible={true}
          accessibilityLabel={'Your Next Rank'}
          testID={Rank.rankProgressTitle}
          variant="label"
          style={styles.leadInText}>
          {isUser
            ? tt('leadIn', 'Your Next Rank')
            : tt('leadInCoach', "Coach's Next Rank")}
        </Text>
      </View>
      <View style={[styles.columns, isLargeFont && styles.columnsWrapped]}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={Rank.rankProgressDescription}
          variant="heading"
          style={[styles.progressDescription]}
          accessible={true}
          accessibilityLabel={`Progress: ${progressDescriptionString}`}>
          {progressDescription}
        </Text>
        <View
          {...testProperties(Rank.rankIcon)}
          style={[
            styles.rankIconColumn,
            isLargeFont && styles.rankIconColumnWrapped,
          ]}>
          <RankIcon
            rankId={rankId}
            scale={rankIconScale}
            style={
              isLargeFont
                ? {
                    shield: styles.rankIconShieldWrapped,
                    icon: styles.rankIconGlyphWrapped,
                  }
                : undefined
            }
          />
        </View>
      </View>
    </View>
  );
};

export default withTranslation('rankProgressCard.summary')(Summary);
